import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX, HiOutlineTrash } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import ConfirmDialog from "components/ConfirmDialog";

import {
  RENEWAL_ADDRESSEE_DESIGNATIONS,
  RENEWAL_APPLICANT_TYPES,
  RENEWAL_CLASS_NUMBERS,
  RENEWAL_FORM_OF_ADDRESS_OPTIONS,
  RENEWAL_PLACE_OPTIONS,
  RENEWAL_TYPE_OF_MARK_OPTIONS,
  deleteRenewalDocument,
  generateRenewalUploadPresignedUrls,
  getRenewalDiscussionDetails,
  registerRenewalDocuments,
  submitRenewalApplicationDetails,
  type RenewalApplicationInput,
} from "../../api/renewal";
import { getLeadPrefill } from "../../api/assignment";

export const RENEWAL_APPLICATION_DETAILS_FORM_ID =
  "renewal-application-details-form";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp,.pdf";
const ACCEPTED_HELP = "JPEG, PNG, PDF formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const FILE_INPUT_ID = "renewal-application-details-file-input";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const REQ = <span className="text-red-500 ml-0.5">*</span>;
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "w-full min-h-[64px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border resize-y";

type SavedDocument = {
  documentId: string;
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
};

type EditableApplication = {
  rowId: string;
  tmNo: string;
  dateOfFiling: Date | null;
  mark: string;
  typeOfMark: string;
  classNumber: string;
  validityDate: Date | null;
  userDate: Date | null;
};

let rowIdCounter = 0;
function makeRowId(): string {
  rowIdCounter += 1;
  return `app-row-${rowIdCounter}-${Date.now()}`;
}

function makeEmptyApplication(defaultMark: string = ""): EditableApplication {
  return {
    rowId: makeRowId(),
    tmNo: "",
    dateOfFiling: null,
    mark: defaultMark,
    typeOfMark: "",
    classNumber: "",
    validityDate: null,
    userDate: null,
  };
}

function parseDateInput(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Renewal backend stores `addressee` as a single string. The UI surfaces a
// designation dropdown matching other modules; when the user picks NAME we
// reveal a free-text input. The value persisted is whichever is in effect.
function splitAddressee(stored: string | null | undefined): {
  designation: string;
  customName: string;
} {
  const value = (stored ?? "").trim();
  if (!value) return { designation: "", customName: "" };
  const designations = RENEWAL_ADDRESSEE_DESIGNATIONS as readonly string[];
  if (designations.includes(value) && value !== "NAME") {
    return { designation: value, customName: "" };
  }
  return { designation: "NAME", customName: value };
}

function joinAddressee(designation: string, customName: string): string {
  if (!designation) return "";
  if (designation === "NAME") return customName.trim();
  return designation;
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSubmittingChange?: (submitting: boolean) => void;
};

export default function RenewalApplicationDetailsFromClientView({
  leadId,
  onSuccess,
  onSubmittingChange,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    onSubmittingChange?.(saving);
    return () => onSubmittingChange?.(false);
  }, [saving, onSubmittingChange]);

  // Addressee
  const [addresseeDesignation, setAddresseeDesignation] = useState("");
  const [addresseeCustomName, setAddresseeCustomName] = useState("");
  const [formOfAddress, setFormOfAddress] = useState("");
  const [firmName, setFirmName] = useState("");
  const [place, setPlace] = useState("");
  const [placeManualMode, setPlaceManualMode] = useState(false);

  // Applicant block
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");

  // Documents (global to the lead — renewal does not associate per-app)
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Applications
  const [applications, setApplications] = useState<EditableApplication[]>([
    makeEmptyApplication(),
  ]);
  const [preliminaryMark, setPreliminaryMark] = useState<string>("");
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | null>(null);

  // Inline validation
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const hasError = (key: string) => errors.has(key);
  const errorBorder = (key: string) => (hasError(key) ? " !border-red-500" : "");
  const clearError = (key: string) => {
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const prefilledRef = useRef(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getRenewalDiscussionDetails(leadId)
      .then(async (details) => {
        if (cancelled) return;

        const prelimMark = details.preliminary?.markName ?? "";
        setPreliminaryMark(prelimMark);

        const d = details.applicationDetails;
        if (d) {
          const { designation, customName } = splitAddressee(d.addressee);
          setAddresseeDesignation(designation);
          setAddresseeCustomName(customName);
          setFormOfAddress(d.formOfAddress ?? "");
          setFirmName(d.firmName ?? "");
          setPlace(d.place ?? "");
          setApplicant(d.applicant ?? "");
          setApplicantAddress(d.applicantAddress ?? "");
          setApplicantType(d.applicantType ?? "");
          setSignatoryName(d.signatoryName ?? "");
          setSignatoryDesignation(d.signatoryDesignation ?? "");
        }

        setSavedDocuments(
          (details.documents ?? []).map((doc) => ({
            documentId: doc.documentId,
            fileKey: doc.fileKey,
            fileName: doc.filename,
            fileUrl: doc.downloadUrl ?? null,
          }))
        );

        if (details.applications.length > 0) {
          setApplications(
            details.applications.map((a) => ({
              rowId: makeRowId(),
              tmNo: a.tmNo ?? "",
              dateOfFiling: parseDateInput(a.dateOfFiling),
              mark: prelimMark || a.mark || "",
              typeOfMark: a.typeOfMark ?? "",
              classNumber: a.classNumber != null ? String(a.classNumber) : "",
              validityDate: parseDateInput(a.validityDate),
              userDate: parseDateInput(a.userDate),
            }))
          );
        } else if (prelimMark) {
          setApplications([makeEmptyApplication(prelimMark)]);
        }

        // Prefill from reference lead when nothing has been saved yet.
        const hasSavedAppDetails = Boolean(d && (d.applicant || d.applicantAddress));
        const hasSavedApplications = details.applications.length > 0;
        if (
          !prefilledRef.current &&
          details.referenceLeadId &&
          !hasSavedAppDetails
        ) {
          prefilledRef.current = true;
          try {
            const prefill = await getLeadPrefill(details.referenceLeadId);
            if (cancelled) return;
            if (!hasSavedAppDetails) {
              if (prefill.applicant) setApplicant(prefill.applicant);
              if (prefill.applicantAddress) setApplicantAddress(prefill.applicantAddress);
              if (prefill.applicantType) setApplicantType(prefill.applicantType);
              if (prefill.signatoryName) setSignatoryName(prefill.signatoryName);
              if (prefill.signatoryDesignation)
                setSignatoryDesignation(prefill.signatoryDesignation);
              if (prefill.addressee?.formOfAddress)
                setFormOfAddress(prefill.addressee.formOfAddress);
              if (prefill.addressee?.districtOfApplicant)
                setPlace(prefill.addressee.districtOfApplicant);
              const line1 = prefill.addressee?.addresseeLine1?.trim();
              if (line1) {
                if ((RENEWAL_ADDRESSEE_DESIGNATIONS as readonly string[]).includes(line1)) {
                  setAddresseeDesignation(line1);
                } else {
                  setAddresseeDesignation("NAME");
                  setAddresseeCustomName(line1);
                }
              }
              const line2 = prefill.addressee?.addresseeLine2?.trim();
              if (line2) setFirmName(line2);
            }
            if (
              !hasSavedApplications &&
              Array.isArray(prefill.applications) &&
              prefill.applications.length > 0
            ) {
              setApplications(
                prefill.applications.map((a) => ({
                  rowId: makeRowId(),
                  tmNo: a.tmNo ?? "",
                  dateOfFiling: parseDateInput(a.applDate),
                  mark: prelimMark || a.mark || "",
                  typeOfMark: a.typeOfMark ?? "",
                  classNumber: a.classNumber != null ? String(a.classNumber) : "",
                  validityDate: parseDateInput(a.validityDate),
                  userDate: parseDateInput(a.userDate?.date),
                }))
              );
            }
          } catch {
            // prefill is best-effort; ignore errors
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load application details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const updateApplication = (rowId: string, patch: Partial<EditableApplication>) => {
    setApplications((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
    for (const field of Object.keys(patch)) {
      clearError(`app:${rowId}:${field}`);
    }
  };

  const onFilesSelected = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const accepted: File[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setPendingFiles((prev) => [...prev, ...accepted]);
    clearError("files");
  };

  const removePendingFile = (idx: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeSavedDocument = async (idx: number) => {
    const doc = savedDocuments[idx];
    if (!doc || !leadId) return;
    try {
      await deleteRenewalDocument(leadId, doc.documentId);
      setSavedDocuments((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Document removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove document");
    }
  };

  const openPendingPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    setPreview({
      url,
      name: file.name,
      kind: detectPreviewKind(file.name, file.type),
      revokeOnClose: true,
    });
  };

  const openSavedPreview = (doc: SavedDocument) => {
    if (!doc.fileUrl) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({
      url: doc.fileUrl,
      name: doc.fileName,
      kind: detectPreviewKind(doc.fileName),
    });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const totalFileCount = pendingFiles.length + savedDocuments.length;

  const addApplicationRow = () => {
    setApplications((prev) => [...prev, makeEmptyApplication(preliminaryMark)]);
  };

  const removeApplicationRow = (rowId: string) => {
    setApplications((prev) => {
      const next = prev.filter((row) => row.rowId !== rowId);
      return next.length === 0 ? [makeEmptyApplication(preliminaryMark)] : next;
    });
  };

  const showAddresseeCustomName = addresseeDesignation === "NAME";

  const designationOptions = useMemo(
    () =>
      (RENEWAL_ADDRESSEE_DESIGNATIONS as readonly string[]).map((d) => ({
        value: d,
        label: d === "NAME" ? "Name (Other)" : d,
      })),
    []
  );

  const placeIsManual =
    placeManualMode ||
    (place !== "" &&
      !RENEWAL_PLACE_OPTIONS.includes(place as (typeof RENEWAL_PLACE_OPTIONS)[number]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const next = new Set<string>();
    if (!addresseeDesignation) next.add("addresseeDesignation");
    if (addresseeDesignation === "NAME" && !addresseeCustomName.trim())
      next.add("addresseeCustomName");
    if (!formOfAddress) next.add("formOfAddress");
    if (!firmName.trim()) next.add("firmName");
    if (!place.trim()) next.add("place");
    if (!applicant.trim()) next.add("applicant");
    if (!applicantType) next.add("applicantType");
    if (!applicantAddress.trim()) next.add("applicantAddress");
    if (!signatoryName.trim()) next.add("signatoryName");
    if (!signatoryDesignation.trim()) next.add("signatoryDesignation");

    for (const row of applications) {
      if (!row.tmNo.trim()) next.add(`app:${row.rowId}:tmNo`);
      if (!row.dateOfFiling) next.add(`app:${row.rowId}:dateOfFiling`);
      if (!row.mark.trim()) next.add(`app:${row.rowId}:mark`);
      if (!row.typeOfMark) next.add(`app:${row.rowId}:typeOfMark`);
      if (!row.classNumber) next.add(`app:${row.rowId}:classNumber`);
      if (!row.validityDate) next.add(`app:${row.rowId}:validityDate`);
    }

    if (next.size > 0) {
      setErrors(next);
      return;
    }
    setErrors(new Set());

    setSaving(true);
    try {
      // Upload any pending files first — renewal uses a dedicated presign +
      // register pair (not inline like assignment).
      let registeredDocs: Array<{ fileKey: string; filename: string }> = [];
      if (pendingFiles.length > 0) {
        const presigned = await generateRenewalUploadPresignedUrls(
          leadId,
          pendingFiles.map((file) => ({
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
          }))
        );
        if (presigned.length !== pendingFiles.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          pendingFiles.map(async (file, i) => {
            const target = presigned[i];
            const putRes = await fetch(target.url, {
              method: "PUT",
              body: file,
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
            });
            if (!putRes.ok) throw new Error(`Failed to upload ${file.name}`);
          })
        );
        registeredDocs = pendingFiles.map((file, i) => ({
          fileKey: presigned[i].key,
          filename: file.name,
        }));
        await registerRenewalDocuments(leadId, registeredDocs);
      }

      const addresseeValue = joinAddressee(addresseeDesignation, addresseeCustomName);

      await submitRenewalApplicationDetails(leadId, {
        addressee: addresseeValue || undefined,
        formOfAddress: formOfAddress || undefined,
        firmName: firmName.trim() || undefined,
        place: place.trim() || undefined,
        applicant: applicant.trim() || undefined,
        applicantAddress: applicantAddress.trim() || undefined,
        applicantType: applicantType || undefined,
        signatoryName: signatoryName.trim() || undefined,
        signatoryDesignation: signatoryDesignation.trim() || undefined,
        applications: applications
          .filter((row) => row.tmNo.trim().length > 0)
          .map<RenewalApplicationInput>((row) => ({
            tmNo: row.tmNo.trim(),
            dateOfFiling: toISODateString(row.dateOfFiling),
            mark: row.mark.trim() || null,
            typeOfMark: row.typeOfMark || null,
            classNumber:
              row.classNumber && !isNaN(Number(row.classNumber))
                ? Number(row.classNumber)
                : null,
            validityDate: toISODateString(row.validityDate),
            userDate: toISODateString(row.userDate),
          })),
      });

      // Move just-uploaded files into the saved list. We don't yet have their
      // document IDs (the register call returns only a count); the next reload
      // of this view will surface them with proper IDs.
      if (registeredDocs.length > 0) {
        setPendingFiles([]);
      }

      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save application details"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this
          step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={RENEWAL_APPLICATION_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* --- Addressee --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Addressee</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Addressee Designation{REQ}</label>
            <Select
              value={addresseeDesignation}
              onValueChange={(v) => {
                setAddresseeDesignation(v);
                clearError("addresseeDesignation");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("addresseeDesignation")}>
                <SelectValue placeholder="Select Designation" />
              </SelectTrigger>
              <SelectContent>
                {designationOptions.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showAddresseeCustomName && (
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Name{REQ}</label>
              <input
                type="text"
                value={addresseeCustomName}
                onChange={(e) => {
                  setAddresseeCustomName(e.target.value);
                  clearError("addresseeCustomName");
                }}
                placeholder="Enter name"
                className={INPUT_CLASS + errorBorder("addresseeCustomName")}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Form of Address{REQ}</label>
            <Select
              value={formOfAddress}
              onValueChange={(v) => {
                setFormOfAddress(v);
                clearError("formOfAddress");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("formOfAddress")}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {RENEWAL_FORM_OF_ADDRESS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Firm Name{REQ}</label>
            <input
              type="text"
              value={firmName}
              onChange={(e) => {
                setFirmName(e.target.value);
                clearError("firmName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("firmName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className={LABEL_CLASS}>Place{REQ}</label>
              {placeIsManual && (
                <button
                  type="button"
                  onClick={() => {
                    setPlaceManualMode(false);
                    setPlace("");
                    clearError("place");
                  }}
                  className="text-[11px] font-normal text-[#0070F0] hover:underline"
                >
                  Choose from list
                </button>
              )}
            </div>
            {placeIsManual ? (
              <input
                type="text"
                value={place}
                onChange={(e) => {
                  setPlace(e.target.value);
                  clearError("place");
                }}
                placeholder="Enter place"
                className={INPUT_CLASS + errorBorder("place")}
                autoFocus={placeManualMode && !place}
              />
            ) : (
              <Select
                value={place}
                onValueChange={(v) => {
                  if (v === "__OTHER__") {
                    setPlaceManualMode(true);
                    setPlace("");
                    clearError("place");
                    return;
                  }
                  setPlace(v);
                  clearError("place");
                }}
              >
                <SelectTrigger className={INPUT_CLASS + errorBorder("place")}>
                  <SelectValue placeholder="Select Place" />
                </SelectTrigger>
                <SelectContent>
                  {RENEWAL_PLACE_OPTIONS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                  <SelectItem value="__OTHER__">Other (Enter manually)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* --- Applicant --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Applicant Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant{REQ}</label>
            <input
              type="text"
              value={applicant}
              onChange={(e) => {
                setApplicant(e.target.value);
                clearError("applicant");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("applicant")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Type{REQ}</label>
            <Select
              value={applicantType}
              onValueChange={(v) => {
                setApplicantType(v);
                clearError("applicantType");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("applicantType")}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {RENEWAL_APPLICANT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1 md:col-span-2">
            <label className={LABEL_CLASS}>Applicant Address{REQ}</label>
            <textarea
              value={applicantAddress}
              onChange={(e) => {
                setApplicantAddress(e.target.value);
                clearError("applicantAddress");
              }}
              placeholder="Address"
              className={TEXTAREA_CLASS + errorBorder("applicantAddress")}
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory's Name{REQ}</label>
            <input
              type="text"
              value={signatoryName}
              onChange={(e) => {
                setSignatoryName(e.target.value);
                clearError("signatoryName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("signatoryName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory's Designation{REQ}</label>
            <input
              type="text"
              value={signatoryDesignation}
              onChange={(e) => {
                setSignatoryDesignation(e.target.value);
                clearError("signatoryDesignation");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("signatoryDesignation")}
            />
          </div>
        </div>
      </div>

      {/* --- Supporting Documents (lead-level; renewal does not associate per-app) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            Supporting Documents
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (saving) return;
              document.getElementById(FILE_INPUT_ID)?.click();
            }}
            className="h-9 px-3 inline-flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] text-[12px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)" }}
          >
            Select Files
          </button>
        </div>
        <p className="text-[11px] text-[#6D6D6D] -mt-3">{ACCEPTED_HELP}</p>

        <input
          id={FILE_INPUT_ID}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onFilesSelected(e.target.files);
            }
            e.currentTarget.value = "";
          }}
        />

        {totalFileCount > 0 && (
          <div className="flex flex-col gap-2 self-stretch">
            {savedDocuments.map((doc, dIdx) => (
              <div
                key={`saved-${doc.documentId}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                  {doc.fileName}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openSavedPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  {doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName}
                      className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </a>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${doc.fileName}`}
                    onClick={() => removeSavedDocument(dIdx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {pendingFiles.map((file, pIdx) => (
              <div
                key={`pending-${pIdx}-${file.name}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
              >
                <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                  {file.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openPendingPreview(file)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removePendingFile(pIdx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- TM Applications (grid-table layout) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">TM Applications</p>
          <button
            type="button"
            onClick={addApplicationRow}
            className="text-sm font-normal text-[#0070F0] hover:underline"
          >
            + Add Application
          </button>
        </div>

        <div>
          <div className="flex flex-col">
            <div className="grid grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)_120px_140px_140px_28px] gap-3 px-1 pb-2">
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Application No.{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Date of Filing{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Type of Mark{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Class Number{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Validity Date{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">User Date</div>
              <div />
            </div>

            {applications.map((row) => (
              <div
                key={row.rowId}
                className="flex flex-col gap-2 py-3 border-t border-[#EEEEEE] first:border-t-0"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)_120px_140px_140px_28px] gap-3 px-1 items-start">
                  <input
                    type="text"
                    value={row.tmNo}
                    onChange={(e) => updateApplication(row.rowId, { tmNo: e.target.value })}
                    placeholder="e.g. 1234567"
                    className={INPUT_CLASS + errorBorder(`app:${row.rowId}:tmNo`)}
                  />
                  <DateTimePicker
                    value={row.dateOfFiling}
                    onChange={(date) => updateApplication(row.rowId, { dateOfFiling: date })}
                    placeholder="Select Date"
                    buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:dateOfFiling`)}
                  />
                  <Select
                    value={row.typeOfMark}
                    onValueChange={(v) => updateApplication(row.rowId, { typeOfMark: v })}
                  >
                    <SelectTrigger className={INPUT_CLASS + errorBorder(`app:${row.rowId}:typeOfMark`)}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {RENEWAL_TYPE_OF_MARK_OPTIONS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={row.classNumber}
                    onValueChange={(v) => updateApplication(row.rowId, { classNumber: v })}
                  >
                    <SelectTrigger className={INPUT_CLASS + errorBorder(`app:${row.rowId}:classNumber`)}>
                      <SelectValue placeholder="Select Class" />
                    </SelectTrigger>
                    <SelectContent>
                      {RENEWAL_CLASS_NUMBERS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <DateTimePicker
                    value={row.validityDate}
                    onChange={(date) => updateApplication(row.rowId, { validityDate: date })}
                    placeholder="Select Date"
                    buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:validityDate`)}
                  />
                  <DateTimePicker
                    value={row.userDate}
                    onChange={(date) => updateApplication(row.rowId, { userDate: date })}
                    placeholder="Select Date"
                    buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:userDate`)}
                  />
                  <div className="flex items-center justify-center h-9">
                    {applications.length > 1 ? (
                      <button
                        type="button"
                        aria-label="Remove application"
                        onClick={() => setPendingDeleteRowId(row.rowId)}
                        className="text-[#6D6D6D] hover:text-red-600 p-1"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />

      <ConfirmDialog
        open={!!pendingDeleteRowId}
        title="Delete Application"
        description="Are you sure you want to delete this application? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (pendingDeleteRowId) removeApplicationRow(pendingDeleteRowId);
          setPendingDeleteRowId(null);
        }}
        onClose={() => setPendingDeleteRowId(null)}
      />
    </form>
  );
}
